import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { TokenStorageService } from '../services/token-storage.service';

type ActivateReturnValue = Promise<boolean | UrlTree>;

type LoadReturnValue = Promise<boolean>;

@Injectable({
  providedIn: 'root',
})
export class AuthGuard  {
  private authService = inject(AuthService);
  private router = inject(Router);
  private tokenStorage = inject(TokenStorageService);


  pathToNext(next: ActivatedRouteSnapshot) {
    const relativePath = next.url
      .map((s: UrlSegment) => s.toString())
      .join('/');
    return `/${relativePath}`;
  }

  pathToRoute(route: Route, segments: UrlSegment[]): string | undefined {
    const relativePath = segments
      .map((s: UrlSegment) => s.toString())
      .join('/');
    return `${route.path}/${relativePath}`;
  }

  routeToLogin(path: string | undefined) {
    if (path && path != '/') {
      const extras = { queryParams: { next: path } };
      this.router.navigate(['/login'], extras);
    } else {
      this.router.navigate(['/login']);
    }
  }

  // get the roles which can access the next route
  getRequiredRoles(next: ActivatedRouteSnapshot): string[] {
    const organization_id = next.params.organization_id;
    const userRoles = next.data.roles ? next.data.roles : [];
    const organizationRoles =
      organization_id && next.data.organizationRoles
        ? next.data.organizationRoles.map((role: string) =>
            AuthService.buildOrgRole(organization_id, role)
          )
        : [];

    return [...userRoles, ...organizationRoles];
  }

  async canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): ActivateReturnValue {
    const token = this.tokenStorage.getToken();
    if (!token) {
      this.routeToLogin(this.pathToNext(next));
      return false;
    }

    const requiredRoles: string[] = this.getRequiredRoles(next);
    const hasRequiredRoles = requiredRoles && requiredRoles.length > 0;
    if (hasRequiredRoles && !this.authService.hasAnyRole(requiredRoles)) {
      this.router.navigate(['/']);
      return false;
    }

    return true;
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): ActivateReturnValue {
    return this.canActivate(next, state);
  }

  async canLoad(route: Route, segments: UrlSegment[]): LoadReturnValue {
    const token = this.tokenStorage.getToken();
    if (!token) {
      this.routeToLogin(this.pathToRoute(route, segments));
      return false;
    }

    return true;
  }
}
